import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Eye, 
  CheckCircle, 
  XCircle, 
  Ban, 
  Trash2, 
  Search,
  Filter,
  Plus,
  Mail,
  Phone,
  Building2,
  MoreVertical,
  ChevronDown
} from "lucide-react";
import { format } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLanguage } from "@/contexts/LanguageContext";
import OptimizedImage from "@/components/ui/OptimizedImage";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminClients, useUpdateClientStatus, useDeleteClient } from "@/hooks/api/use-admin";

const AdminClients = () => {
  const { t, isRTL } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.type === 'admin';

  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected' | 'suspended'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [isReasonDialogOpen, setIsReasonDialogOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<'rejected' | 'suspended' | null>(null);
  const [reason, setReason] = useState('');
  const observerTarget = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const filters = {
    limit: 15,
    ...(statusFilter !== 'all' && { status: statusFilter }),
    ...(debouncedSearchQuery && { search: debouncedSearchQuery }),
  };

  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useAdminClients(
    filters,
    { enabled: isAdmin && !!user }
  );

  const updateStatusMutation = useUpdateClientStatus();
  const deleteMutation = useDeleteClient();


  // Flatten paginated data
  const clients = data?.pages?.flatMap((page) => {
    const pageData = page?.data;
    if (!pageData) return [];
    
    if (Array.isArray(pageData.data)) {
      return pageData.data;
    }
    
    if (Array.isArray(pageData)) {
      return pageData;
    }
    
    return [];
  }) || [];

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleViewDetails = (clientId: number) => {
    navigate(`/admin/dashboard/clients/${clientId}`);
  };

  const handleUpdateStatus = (clientId: number, status: 'approved' | 'rejected' | 'suspended') => {
    if (status === 'rejected' || status === 'suspended') {
      setSelectedClientId(clientId);
      setSelectedStatus(status);
      setReason('');
      setIsReasonDialogOpen(true);
    } else {
      updateStatusMutation.mutate(
        { id: clientId, data: { status } },
        {
          onSuccess: () => {
            // Refetch clients list to update status
            refetch();
          },
        }
      );
    }
  };

  const handleDelete = (clientId: number) => {
    if (confirm(t('admin.clients.confirmDelete'))) {
      deleteMutation.mutate(clientId, {
        onSuccess: () => {
          // Refetch clients list after deletion
          refetch();
        },
      });
    }
  };


  const handleReasonSubmit = () => {
    if (selectedClientId && selectedStatus && reason.trim()) {
      updateStatusMutation.mutate(
        { id: selectedClientId, data: { status: selectedStatus, reason } },
        {
          onSuccess: () => {
            setIsReasonDialogOpen(false);
            setSelectedClientId(null);
            setSelectedStatus(null);
            setReason('');
            refetch();
          },
        }
      );
    }
  };

  const handleReasonCancel = () => {
    setIsReasonDialogOpen(false);
    setSelectedClientId(null);
    setSelectedStatus(null);
    setReason('');
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: {
        label: t('common.pending') || 'قيد المراجعة',
        className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      },
      approved: {
        label: t('common.approved') || 'معتمد',
        className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      },
      rejected: {
        label: t('common.rejected') || 'مرفوض',
        className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      },
      suspended: {
        label: t('admin.clients.suspended') || 'معلق',
        className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;

    return (
      <Badge className={config.className}>
        {config.label}
      </Badge>
    );
  };

  // Only admin can access
  if (!isAdmin) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <p className="text-muted-foreground">
            {t('admin.clients.adminOnly') || 'هذه الصفحة متاحة فقط للمسؤولين'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {t('admin.clients.title') || 'إدارة العملاء'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('admin.clients.subtitle') || 'عرض وإدارة جميع العملاء في النظام'}
          </p>
        </div>
        <Button
          onClick={() => navigate('/admin/dashboard/clients/create')}
          className="flex items-center gap-2"
        >
          <Plus size={16} />
          {t('admin.clients.createClient') || 'إنشاء عميل جديد'}
        </Button>
      </div>

      {/* Reason Dialog */}
      <Dialog open={isReasonDialogOpen} onOpenChange={setIsReasonDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedStatus === 'rejected' 
                ? (t('admin.clients.rejectReason') || 'سبب الرفض')
                : (t('admin.clients.suspendReason') || 'سبب التعليق')
              }
            </DialogTitle>
            <DialogDescription>
              {selectedStatus === 'rejected' 
                ? (t('admin.clients.rejectReasonDesc') || 'يرجى إدخال سبب رفض العميل')
                : (t('admin.clients.suspendReasonDesc') || 'يرجى إدخال سبب تعليق العميل')
              }
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reason">
                  {selectedStatus === 'rejected' 
                    ? (t('admin.clients.rejectReason') || 'سبب الرفض')
                    : (t('admin.clients.suspendReason') || 'سبب التعليق')
                  }
                </Label>
                <Textarea
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={
                    selectedStatus === 'rejected' 
                      ? (t('admin.clients.rejectReasonPlaceholder') || 'أدخل سبب رفض العميل...')
                      : (t('admin.clients.suspendReasonPlaceholder') || 'أدخل سبب تعليق العميل...')
                  }
                  rows={4}
                  className="resize-none"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleReasonCancel}
            >
              {t('common.cancel') || 'إلغاء'}
            </Button>
            <Button
              type="button"
              onClick={handleReasonSubmit}
              disabled={!reason.trim() || updateStatusMutation.isPending}
            >
              {updateStatusMutation.isPending 
                ? (t('common.loading') || 'جاري التحديث...')
                : (selectedStatus === 'rejected' 
                  ? (t('admin.clients.reject') || 'رفض')
                  : (t('admin.clients.suspend') || 'تعليق')
                )
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4">
            {/* Search */}
            <div className="relative">
              <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-muted-foreground`} size={18} />
              <Input
                placeholder={t('admin.clients.searchPlaceholder') || 'البحث عن عميل...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`${isRTL ? 'pr-10' : 'pl-10'} w-full`}
              />
            </div>

            {/* Status Filter */}
            <div className="w-full">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    <Filter size={16} className={`${isRTL ? 'ml-2' : 'mr-2'}`} />
                    {statusFilter === 'all' 
                      ? (t('admin.clients.allStatuses') || 'جميع الحالات')
                      : statusFilter === 'pending'
                      ? (t('common.pending') || 'قيد المراجعة')
                      : statusFilter === 'approved'
                      ? (t('common.approved') || 'معتمد')
                      : statusFilter === 'rejected'
                      ? (t('common.rejected') || 'مرفوض')
                      : (t('admin.clients.suspended') || 'معلق')
                    }
                    <ChevronDown size={16} className={`${isRTL ? 'mr-2' : 'ml-2'} opacity-50`} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align={isRTL ? "start" : "end"} className="w-full max-w-xs">
                  <DropdownMenuItem onClick={() => setStatusFilter('all')}>
                    {t('admin.clients.allStatuses') || 'جميع الحالات'}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setStatusFilter('pending')}>
                    {t('common.pending') || 'قيد المراجعة'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setStatusFilter('approved')}>
                    {t('common.approved') || 'معتمد'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setStatusFilter('rejected')}>
                    {t('common.rejected') || 'مرفوض'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setStatusFilter('suspended')}>
                    {t('admin.clients.suspended') || 'معلق'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Clients Table */}
      {isLoading ? (
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      ) : error ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              {t('common.error') || 'حدث خطأ أثناء تحميل العملاء'}
            </p>
          </CardContent>
        </Card>
      ) : clients.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              {t('admin.clients.noClients') || 'لا توجد عملاء'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            {isMobile ? (
              // Mobile Card Layout
              <div className="divide-y">
                {clients.map((client: any) => {
                  const clientName = client.name;
                  const clientEmail = client.email;
                  const companyName = client.company_name;
                  const phone = client.phone;
                  const status = client.status || 'pending';
                  const clientId = client.client_id || client.id;
                  const profileImageUrl = client.profile_image_url;
                  
                  return (
                    <div key={client.id} className="p-4 space-y-3">
                      {/* Header with name, status, and actions */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          {profileImageUrl ? (
                            <OptimizedImage
                              src={profileImageUrl}
                              alt={clientName}
                              containerClassName="w-10 h-10 rounded-full flex-shrink-0"
                              className="w-10 h-10 rounded-full object-cover"
                              fallback={
                                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                  <span className="text-primary font-bold text-sm">
                                    {clientName.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                              }
                            />
                          ) : (
                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-primary font-bold text-sm">
                                {clientName.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <h3 className="font-medium text-sm truncate">{clientName}</h3>
                            {companyName && (
                              <div className="flex items-center gap-1 mt-1">
                                <Building2 size={12} className="text-muted-foreground" />
                                <span className="text-xs text-muted-foreground truncate">{companyName}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {getStatusBadge(status)}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical size={14} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align={isRTL ? "start" : "end"} className="w-48">
                              <DropdownMenuItem onClick={() => handleViewDetails(clientId)}>
                                <Eye size={14} className="mr-2" />
                                {t('admin.clients.viewDetails') || 'عرض التفاصيل'}
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleUpdateStatus(clientId, 'approved')}
                                disabled={updateStatusMutation.isPending}
                              >
                                <CheckCircle size={14} className="mr-2" />
                                {t('admin.clients.approve') || 'اعتماد'}
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleUpdateStatus(clientId, 'rejected')}
                                disabled={updateStatusMutation.isPending}
                              >
                                <XCircle size={14} className="mr-2" />
                                {t('admin.clients.reject') || 'رفض'}
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleUpdateStatus(clientId, 'suspended')}
                                disabled={updateStatusMutation.isPending}
                              >
                                <Ban size={14} className="mr-2" />
                                {t('admin.clients.suspend') || 'تعليق'}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => handleDelete(clientId)}
                                disabled={deleteMutation.isPending}
                                className="text-destructive"
                              >
                                <Trash2 size={14} className="mr-2" />
                                {t('admin.clients.delete') || 'حذف'}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                      
                      {/* Contact Information */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <Mail size={14} className="text-muted-foreground flex-shrink-0" />
                          <span className="truncate">{clientEmail}</span>
                        </div>
                        {phone && (
                          <div className="flex items-center gap-2 text-sm">
                            <Phone size={14} className="text-muted-foreground flex-shrink-0" />
                            <span>{phone}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Created Date */}
                      <div className="text-xs text-muted-foreground">
                        {(() => {
                          try {
                            const date = new Date(client.created_at);
                            return format(date, "PPP", { locale: isRTL ? ar : enUS });
                          } catch {
                            return client.created_at;
                          }
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              // Desktop Table Layout
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('admin.clients.name') || 'الاسم'}</TableHead>
                      <TableHead>{t('admin.clients.companyName') || 'الشركة'}</TableHead>
                      <TableHead>{t('admin.clients.email') || 'البريد الإلكتروني'}</TableHead>
                      <TableHead>{t('admin.clients.phone') || 'رقم الهاتف'}</TableHead>
                      <TableHead>{t('admin.clients.status') || 'الحالة'}</TableHead>
                      <TableHead>{t('admin.clients.createdAt') || 'تاريخ الإنشاء'}</TableHead>
                      <TableHead className="text-right">{t('admin.clients.actions') || 'الإجراءات'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clients.map((client: any) => {
                      const clientName = client.name;
                      const clientEmail = client.email;
                      const companyName = client.company_name;
                      const phone = client.phone;
                      const status = client.status || 'pending';
                      const clientId = client.client_id || client.id;
                      const profileImageUrl = client.profile_image_url;
                      
                      return (
                        <TableRow key={client.id} className="hover:bg-muted/50">
                          <TableCell>
                            <div className="flex items-center gap-3">
                              {profileImageUrl ? (
                                <OptimizedImage
                                  src={profileImageUrl}
                                  alt={clientName}
                                  containerClassName="w-8 h-8 rounded-full flex-shrink-0"
                                  className="w-8 h-8 rounded-full object-cover"
                                  fallback={
                                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                      <span className="text-primary font-bold text-sm">
                                        {clientName.charAt(0).toUpperCase()}
                                      </span>
                                    </div>
                                  }
                                />
                              ) : (
                                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                  <span className="text-primary font-bold text-sm">
                                    {clientName.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                              )}
                              <div className="font-medium">{clientName}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {companyName && (
                              <div className="flex items-center gap-1">
                                <Building2 size={14} />
                                <span className="truncate max-w-xs">{companyName}</span>
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Mail size={14} />
                              <span className="truncate max-w-xs">{clientEmail}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {phone && (
                              <div className="flex items-center gap-1">
                                <Phone size={14} />
                                <span>{phone}</span>
                              </div>
                            )}
                          </TableCell>
                          <TableCell>{getStatusBadge(status)}</TableCell>
                          <TableCell>
                            <div className="text-sm text-muted-foreground">
                              {(() => {
                                try {
                                  const date = new Date(client.created_at);
                                  return format(date, "PPP", { locale: isRTL ? ar : enUS });
                                } catch {
                                  return client.created_at;
                                }
                              })()}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical size={16} />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align={isRTL ? "start" : "end"}>
                                <DropdownMenuItem onClick={() => handleViewDetails(clientId)}>
                                  <Eye size={14} className="mr-2" />
                                  {t('admin.clients.viewDetails') || 'عرض التفاصيل'}
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleUpdateStatus(clientId, 'approved')}
                                  disabled={updateStatusMutation.isPending}
                                >
                                  <CheckCircle size={14} className="mr-2" />
                                  {t('admin.clients.approve') || 'اعتماد'}
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleUpdateStatus(clientId, 'rejected')}
                                  disabled={updateStatusMutation.isPending}
                                >
                                  <XCircle size={14} className="mr-2" />
                                  {t('admin.clients.reject') || 'رفض'}
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleUpdateStatus(clientId, 'suspended')}
                                  disabled={updateStatusMutation.isPending}
                                >
                                  <Ban size={14} className="mr-2" />
                                  {t('admin.clients.suspend') || 'تعليق'}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => handleDelete(clientId)}
                                  disabled={deleteMutation.isPending}
                                  className="text-destructive"
                                >
                                  <Trash2 size={14} className="mr-2" />
                                  {t('admin.clients.delete') || 'حذف'}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
            
            {/* Infinite Scroll Trigger */}
            {hasNextPage && (
              <div ref={observerTarget} className="py-4">
                {isFetchingNextPage && (
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminClients;
